import { Button, notify, Switch } from '@affine/component';
import { useGuard } from '@affine/core/components/guard/use-guard';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { getOfflineStatusCopy } from '@affine/core/components/offline-status';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { DocService } from '@affine/core/modules/doc';
import { AccessibleFolderCacheStore } from '@affine/core/modules/organize';
import {
  DocGrantedUsersService,
  type Member,
  WorkspaceMembersService,
} from '@affine/core/modules/permissions';
import { PermissionRoleMenu } from '@affine/core/modules/permissions/components/permission-role-menu';
import {
  createObjectPermissionRoleOptions,
  DOC_SHARE_MEMBER_ROLES,
  isDocRoleWithinWorkspaceCeiling,
  objectPermissionRoleLabel,
  useObjectPermissionOnlineOnlyState,
} from '@affine/core/modules/permissions/utils/object-permission-ui';
import {
  PermissionMemberInviteSearch,
  PermissionMemberRow,
} from '@affine/core/modules/permissions/views/permission-invite-picker';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import {
  StreamLainDisclosure,
  StreamLainDisclosureStyles,
} from '@affine/core/modules/streamlain/native-app';
import { UserFriendlyError } from '@affine/error';
import { DocRole, Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { ArrowLeftBigIcon, CloseIcon } from '@blocksuite/icons/rc';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import {
  type KeyboardEventHandler,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { isDocInsidePrivateSystemFolder } from '../doc-share-restrictions';
import {
  buildTtlOptions,
  formatTtlHours,
  isGuestQuotaFull,
  isValidEmail,
} from './guest-invite-utils';
import * as styles from './invite-member-editor.css';

const formatTtl = (hours: number, t: ReturnType<typeof useI18n>): string => {
  const result = formatTtlHours(hours);
  if (result.days !== undefined) {
    return t['com.streamlain.share-menu.invite-editor.guest-ttl-days']({
      days: String(result.days),
    });
  }
  return t['com.streamlain.share-menu.invite-editor.guest-ttl-hours']({
    hours: String(result.hours),
  });
};

export const InviteMemberEditor = ({
  onClickCancel,
  folderId,
  guestOnly = false,
  disabled = false,
}: {
  onClickCancel: () => void;
  folderId?: string;
  guestOnly?: boolean;
  disabled?: boolean;
}) => {
  const t = useI18n();
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
  const docGrantedUsersService = useService(DocGrantedUsersService);
  const workspaceMembersService = useService(WorkspaceMembersService);
  const docService = useService(DocService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleFolders = useLiveData(
    useMemo(
      () =>
        LiveData.from(accessibleFolderCache.watchAllAccessibleFolders(), []),
      [accessibleFolderCache]
    )
  );
  const isFolderCacheLocalLoaded = useLiveData(
    accessibleFolderCache.isLocalCacheLoaded$
  );
  const isFolderCachePopulated = useLiveData(
    accessibleFolderCache.isPopulated$
  );
  const isFolderCacheReady =
    Boolean(folderId) ||
    (isFolderCacheLocalLoaded === true && isFolderCachePopulated === true);
  const workspaceQuotaService = useService(WorkspaceQuotaService);
  const canManageWorkspaceUsers = useGuard('Workspace_Users_Manage');
  const [inviteDocRoleType, setInviteDocRoleType] = useState<DocRole>(
    DocRole.Manager
  );

  // Guest mode state
  const [isGuestMode, setIsGuestMode] = useState(guestOnly);
  const [guestEmails, setGuestEmails] = useState<string[]>([]);
  const [guestEmailInput, setGuestEmailInput] = useState('');
  const guestRole = DocRole.Reader;

  // Quota data
  const guestEnabled = useLiveData(workspaceQuotaService.quota.guestEnabled$);
  const guestCount = useLiveData(workspaceQuotaService.quota.guestCount$);
  const guestLimit = useLiveData(workspaceQuotaService.quota.guestLimit$);
  const guestMinTtlHours = useLiveData(
    workspaceQuotaService.quota.guestMinTtlHours$
  );
  const guestMaxTtlHours = useLiveData(
    workspaceQuotaService.quota.guestMaxTtlHours$
  );
  const guestDefaultTtlHours = useLiveData(
    workspaceQuotaService.quota.guestDefaultTtlHours$
  );

  const [selectedTtlHours, setSelectedTtlHours] = useState<number | null>(null);

  // Initialize TTL when default becomes available
  useEffect(() => {
    if (guestDefaultTtlHours !== null && selectedTtlHours === null) {
      setSelectedTtlHours(guestDefaultTtlHours);
    }
  }, [guestDefaultTtlHours, selectedTtlHours]);

  const ttlOptions = useMemo(() => {
    if (guestMinTtlHours === null || guestMaxTtlHours === null) return [];
    return buildTtlOptions(guestMinTtlHours, guestMaxTtlHours);
  }, [guestMinTtlHours, guestMaxTtlHours]);

  const guestQuotaFull = useMemo(
    () => isGuestQuotaFull(guestCount, guestLimit),
    [guestCount, guestLimit]
  );

  useEffect(() => {
    workspaceQuotaService.quota.revalidate();
  }, [workspaceQuotaService]);

  const inputRef = useRef<HTMLInputElement>(null);
  const guestInputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const [searchText, setSearchText] = useState('');
  const permissionOnlineOnly = useObjectPermissionOnlineOnlyState();
  const memberPickerUnavailableMessage = getOfflineStatusCopy(
    t,
    'people-online-only'
  );
  const selectedMemberIds = useMemo(
    () => selectedMembers.map(member => member.id),
    [selectedMembers]
  );
  const workspaceDialogService = useService(WorkspaceDialogService);

  // Regular member invite
  const onInviteMembers = useAsyncCallback(async () => {
    const selectedMemberIds = selectedMembers.map(member => member.id);
    track.$.sharePanel.$.inviteUserDocRole({
      control: 'member list',
      role: inviteDocRoleType,
    });
    try {
      await docGrantedUsersService.grantUsersRole(
        selectedMemberIds,
        inviteDocRoleType
      );
      onClickCancel();
      notify.success({
        title: t['Invitation sent'](),
      });
    } catch (error) {
      const err = UserFriendlyError.fromAny(error);
      notify.error({
        title: t[`error.${err.name}`](err.data),
      });
    }
  }, [
    docGrantedUsersService,
    inviteDocRoleType,
    onClickCancel,
    selectedMembers,
    t,
  ]);

  // Guest invite
  const onInviteGuests = useAsyncCallback(async () => {
    if (guestEmails.length === 0) return;

    const docId = docService.doc.id;
    const permission = Permission.Reader;

    try {
      await workspaceMembersService.inviteMembers(guestEmails, {
        permission,
        isGuest: true,
        docId,
        ttlHours: selectedTtlHours ?? undefined,
      });
      if (guestOnly) {
        setGuestEmails([]);
        setGuestEmailInput('');
      } else {
        onClickCancel();
      }
      notify.success({
        title: t['Invitation sent'](),
      });
    } catch (error) {
      const err = UserFriendlyError.fromAny(error);
      notify.error({
        title: t[`error.${err.name}`](err.data),
      });
    }
  }, [
    docService.doc.id,
    guestEmails,
    guestOnly,
    onClickCancel,
    selectedTtlHours,
    t,
    workspaceMembersService,
  ]);

  const onInvite = isGuestMode ? onInviteGuests : onInviteMembers;
  const privateFolderDocShareBlocked = useMemo(
    () =>
      !folderId &&
      isFolderCacheReady &&
      isDocInsidePrivateSystemFolder(docService.doc.id, accessibleFolders),
    [accessibleFolders, docService.doc.id, folderId, isFolderCacheReady]
  );
  const hasSelection = isGuestMode
    ? guestEmails.length > 0
    : selectedMembers.length > 0;

  const focusInput = useCallback(() => {
    if (isGuestMode) {
      guestInputRef.current?.focus();
    } else {
      inputRef.current?.focus();
    }
  }, [isGuestMode]);

  const onFocus = useCallback(() => {
    setFocused(true);
  }, []);
  const onBlur = useCallback(() => {
    setFocused(false);
  }, []);

  const handleRemoved = useCallback(
    (memberId: string) => {
      setSelectedMembers(prev => prev.filter(member => member.id !== memberId));
      focusInput();
    },
    [focusInput]
  );

  const switchToMemberManagementTab = useCallback(() => {
    workspaceDialogService.open('setting', {
      activeTab: 'workspace:members',
    });
  }, [workspaceDialogService]);

  const handleClickMember = useCallback(
    (member: Member) => {
      if (
        !isDocRoleWithinWorkspaceCeiling(inviteDocRoleType, member.permission)
      ) {
        notify.error({
          title:
            t['com.streamlain.permissions.role-not-available-for-person'](),
        });
        return;
      }
      setSelectedMembers(prev => {
        if (prev.some(m => m.id === member.id)) {
          return prev;
        }
        return [...prev, member];
      });
      setSearchText('');
      focusInput();
    },
    [focusInput, inviteDocRoleType, t]
  );

  const handleRoleChange = useCallback((role: DocRole) => {
    setInviteDocRoleType(role);
  }, []);

  // Guest email handling
  const addGuestEmail = useCallback(
    (raw: string) => {
      const email = raw.trim();
      if (!email) return;
      if (!isValidEmail(email)) {
        notify.error({ title: `Invalid email: ${email}` });
        return;
      }
      if (guestEmails.includes(email)) return;
      setGuestEmails(prev => [...prev, email]);
      setGuestEmailInput('');
    },
    [guestEmails]
  );

  const removeGuestEmail = useCallback(
    (email: string) => {
      setGuestEmails(prev => prev.filter(e => e !== email));
      focusInput();
    },
    [focusInput]
  );

  const handleGuestEmailKeyDown: KeyboardEventHandler<HTMLInputElement> =
    useCallback(
      e => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
          e.preventDefault();
          addGuestEmail(guestEmailInput);
        }
        if (
          e.key === 'Backspace' &&
          guestEmailInput === '' &&
          guestEmails.length > 0
        ) {
          setGuestEmails(prev => prev.slice(0, -1));
        }
      },
      [addGuestEmail, guestEmailInput, guestEmails.length]
    );

  const handleGuestEmailBlur = useCallback(() => {
    if (guestEmailInput.trim()) {
      addGuestEmail(guestEmailInput);
    }
    setFocused(false);
  }, [addGuestEmail, guestEmailInput]);

  const handleGuestToggle = useCallback(
    (checked: boolean) => {
      if (checked && guestQuotaFull) {
        notify.error({
          title: t['com.streamlain.share-menu.invite-editor.guest-quota-full']
            ? t['com.streamlain.share-menu.invite-editor.guest-quota-full']()
            : 'Guest quota reached',
        });
        return;
      }
      setIsGuestMode(checked);
      // Reset selections when toggling
      setSelectedMembers([]);
      setGuestEmails([]);
      setGuestEmailInput('');
      setSearchText('');
    },
    [guestQuotaFull, t]
  );

  return (
    <div className={styles.containerStyle}>
      {!guestOnly ? (
        <div className={styles.headerStyle} onClick={onClickCancel}>
          <ArrowLeftBigIcon className={styles.iconStyle} />
          {t['com.streamlain.share-menu.invite-editor.header']()}
        </div>
      ) : null}
      <div className={styles.memberListStyle}>
        {/* Guest toggle */}
        {!guestOnly && guestEnabled && (
          <div className={styles.guestToggleRow}>
            <span className={styles.guestToggleLabel}>
              {t['com.streamlain.share-menu.invite-editor.guest-toggle']()}
            </span>
            <Switch
              data-testid="share-menu-guest-toggle"
              checked={isGuestMode}
              onChange={handleGuestToggle}
              disabled={!guestEnabled}
            />
          </div>
        )}

        {/* Guest info section */}
        {privateFolderDocShareBlocked && (
          <div className={styles.privateFolderWarning}>
            {t[
              'com.streamlain.share-menu.invite-editor.private-folder-doc-disabled'
            ]()}
          </div>
        )}

        {isGuestMode && (
          <div className={styles.guestInfoRow}>
            <span className={styles.guestInfoText}>
              {t['com.streamlain.share-menu.invite-editor.guest-info']()}
            </span>
            {guestCount !== null && guestLimit !== null && (
              <span className={styles.guestQuotaText}>
                {guestLimit < 0
                  ? t[
                      'com.streamlain.share-menu.invite-editor.guest-quota-unlimited'
                    ]({ count: String(guestCount) })
                  : t['com.streamlain.share-menu.invite-editor.guest-quota']({
                      count: String(guestCount),
                      limit: String(guestLimit),
                    })}
              </span>
            )}
            {!canManageWorkspaceUsers && (
              <span className={styles.privateFolderWarning}>
                {t[
                  'com.streamlain.share-menu.invite-editor.workspace-manage-required'
                ]()}
              </span>
            )}
          </div>
        )}

        {/* Input area */}
        {isGuestMode ? (
          <GuestEmailInput
            emails={guestEmails}
            inputValue={guestEmailInput}
            onInputChange={setGuestEmailInput}
            onKeyDown={handleGuestEmailKeyDown}
            onBlur={handleGuestEmailBlur}
            onFocus={onFocus}
            onRemoveEmail={removeGuestEmail}
            focused={focused}
            inputRef={guestInputRef}
            guestRole={guestRole}
            hasEmails={guestEmails.length > 0}
            t={t}
          />
        ) : (
          <PermissionMemberInviteSearch
            title={t['com.streamlain.share-menu.invite-editor.header']()}
            placeholder={t[
              'com.streamlain.share-menu.invite-editor.placeholder'
            ]()}
            noResultsText={t[
              'com.streamlain.share-menu.invite-editor.no-found'
            ]()}
            offlineMessage={memberPickerUnavailableMessage}
            existingUserIds={selectedMemberIds}
            disabled={permissionOnlineOnly.disabled}
            searchText={searchText}
            onSearchTextChange={setSearchText}
            onSelectMember={handleClickMember}
            roleSlot={
              <RoleSelector
                inviteDocRoleType={inviteDocRoleType}
                workspaceRoles={selectedMembers.map(
                  member => member.permission
                )}
                onRoleChange={handleRoleChange}
              />
            }
            inputRef={inputRef}
            inputTestId="share-menu-member-search-input"
            autoFocus
            onInputFocus={onFocus}
            onInputBlur={onBlur}
            deferSearchWhileComposing
            maxVisibleItems={8}
          >
            {selectedMembers.length ? (
              <div className={styles.selectedMembersList}>
                {selectedMembers.map(member => (
                  <PermissionMemberRow
                    key={member.id}
                    user={{
                      id: member.id,
                      name: member.name ?? '',
                      email: member.email ?? undefined,
                      avatarUrl: member.avatarUrl ?? undefined,
                    }}
                    onRemove={() => handleRemoved(member.id)}
                  />
                ))}
              </div>
            ) : null}
          </PermissionMemberInviteSearch>
        )}

        {/* TTL selector for guest mode */}
        {isGuestMode && ttlOptions.length > 0 && selectedTtlHours !== null && (
          <>
            <StreamLainDisclosureStyles />
            <StreamLainDisclosure
              className={styles.ttlDisclosure}
              summary={
                <span className={styles.ttlSummary}>
                  <span>
                    {t[
                      'com.streamlain.share-menu.invite-editor.guest-ttl-label'
                    ]()}
                  </span>
                  <strong>{formatTtl(selectedTtlHours, t)}</strong>
                </span>
              }
            >
              <div className={styles.ttlOptions}>
                {ttlOptions.map(hours => (
                  <Button
                    key={hours}
                    data-testid={`share-menu-ttl-option-${hours}`}
                    variant={selectedTtlHours === hours ? 'primary' : 'plain'}
                    onClick={() => setSelectedTtlHours(hours)}
                  >
                    {formatTtl(hours, t)}
                  </Button>
                ))}
              </div>
            </StreamLainDisclosure>
          </>
        )}
      </div>
      <div className={styles.footerStyle}>
        {!guestOnly ? (
          <span
            className={styles.manageMemberStyle}
            onClick={switchToMemberManagementTab}
          >
            {t['com.streamlain.share-menu.invite-editor.manage-members']()}
          </span>
        ) : null}
        <div className={styles.buttonsContainer}>
          {!guestOnly ? (
            <Button className={styles.button} onClick={onClickCancel}>
              {t['Cancel']()}
            </Button>
          ) : null}
          <Button
            data-testid="share-menu-invite-confirm-button"
            className={styles.button}
            variant="primary"
            disabled={
              !hasSelection ||
              disabled ||
              !isFolderCacheReady ||
              privateFolderDocShareBlocked ||
              (isGuestMode &&
                (!guestEnabled || guestQuotaFull || !canManageWorkspaceUsers))
            }
            onClick={onInvite}
          >
            {t['com.streamlain.share-menu.invite-editor.invite']()}
          </Button>
        </div>
      </div>
    </div>
  );
};

/** Guest email input with tags */
const GuestEmailInput = ({
  emails,
  inputValue,
  onInputChange,
  onKeyDown,
  onBlur,
  onFocus,
  onRemoveEmail,
  focused,
  inputRef,
  guestRole,
  hasEmails,
  t,
}: {
  emails: string[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onBlur: () => void;
  onFocus: () => void;
  onRemoveEmail: (email: string) => void;
  focused: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  guestRole: DocRole;
  hasEmails: boolean;
  t: ReturnType<typeof useI18n>;
}) => {
  return (
    <div
      className={clsx(styles.InputContainer, {
        focus: focused,
      })}
    >
      <div className={styles.emailInputContainer}>
        {emails.map(email => (
          <span key={email} className={styles.emailTag}>
            {email}
            <span
              className={styles.emailTagRemove}
              onClick={() => onRemoveEmail(email)}
            >
              <CloseIcon />
            </span>
          </span>
        ))}
        <input
          data-testid="share-menu-guest-email-input"
          ref={inputRef}
          type="email"
          value={inputValue}
          onChange={e => onInputChange(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onFocus={onFocus}
          autoFocus
          className={styles.searchInput}
          placeholder={
            emails.length
              ? ''
              : t[
                  'com.streamlain.share-menu.invite-editor.guest-email-placeholder'
                ]()
          }
        />
      </div>
      {hasEmails && <GuestRoleLabel guestRole={guestRole} />}
    </div>
  );
};

/** Guest role is fixed by the workspace-level guest policy. */
const GuestRoleLabel = ({ guestRole }: { guestRole: DocRole }) => {
  const t = useI18n();
  const currentRoleName = useMemo(
    () => objectPermissionRoleLabel(t, guestRole),
    [guestRole, t]
  );
  return (
    <div className={styles.roleSelectorContainer}>
      <span className={styles.guestRoleLabel}>{currentRoleName}</span>
    </div>
  );
};

const RoleSelector = ({
  inviteDocRoleType,
  workspaceRoles,
  onRoleChange,
}: {
  inviteDocRoleType: DocRole;
  workspaceRoles: readonly Permission[];
  onRoleChange: (role: DocRole) => void;
}) => {
  const t = useI18n();
  const currentRoleName = useMemo(
    () => objectPermissionRoleLabel(t, inviteDocRoleType),
    [inviteDocRoleType, t]
  );
  const options = useMemo(
    () =>
      createObjectPermissionRoleOptions(t, DOC_SHARE_MEMBER_ROLES).map(
        option => ({
          ...option,
          disabled: workspaceRoles.some(
            workspaceRole =>
              !isDocRoleWithinWorkspaceCeiling(option.role, workspaceRole)
          ),
        })
      ),
    [t, workspaceRoles]
  );

  return (
    <div className={styles.roleSelectorContainer}>
      <PermissionRoleMenu
        role={inviteDocRoleType}
        label={currentRoleName}
        options={options}
        onSelect={onRoleChange}
        triggerKind="menu-trigger"
        className={styles.menuTriggerStyle}
        contentStyle={{ width: '100%' }}
      />
    </div>
  );
};
